/**
 * Booking-Pricing Integration
 *
 * Wires up booking changes to automatic pricing rule evaluation.
 * This module should be initialized once when the application starts.
 */

import { bookingOccupancyService } from './bookingOccupancyService';
import {
  bookingTriggeredPricingService,
  type BookingChangeAggregation,
} from './bookingTriggeredPricingService';
import { getUnifiedPricingWorkflowService } from '../features/pricing/services/unifiedPricingWorkflowService';
import { PRICING_CONFIG } from '../config/pricingConfig';
import { supabase } from './supabaseClient';

let isInitialized = false;
let unsubscribe: (() => void) | null = null;

/**
 * Initialize the booking-pricing integration.
 * Sets up the subscription chain:
 * BookingOccupancyService -> BookingTriggeredPricingService -> UnifiedPricingWorkflowService
 */
export function initializeBookingPricingIntegration(): void {
  if (isInitialized) {
    console.log('[BookingPricingIntegration] Already initialized');
    return;
  }

  console.log('[BookingPricingIntegration] Initializing...');

  // Set up the repricing callback
  bookingTriggeredPricingService.setRepricingCallback(
    async (propertyId: string, aggregation: BookingChangeAggregation) => {
      console.log(
        `[BookingPricingIntegration] Triggering repricing for property ${propertyId}`,
        {
          created: aggregation.bookingsCreated,
          updated: aggregation.bookingsUpdated,
          cancelled: aggregation.bookingsCancelled,
          affectedDates: aggregation.affectedDates.size,
        }
      );

      // Guard: auto-repricing only makes sense when there are active pricing
      // rules to apply. With no rules, the workflow still recomputes and PUSHES
      // a "base" price to WebHotelier — and that base is read from the live
      // channel rate, not calendar_data.base_rate. So a momentarily-wrong live
      // rate gets re-pushed on every booking sync, a divergent loop that resets
      // hand-set prices. Skip entirely when there are no active rules.
      const { count: activeRuleCount, error: ruleCountError } = await supabase
        .from('pricing_rules')
        .select('id', { count: 'exact', head: true })
        .eq('property_id', propertyId)
        .eq('active', true);

      if (ruleCountError) {
        throw new Error(
          `Could not verify active pricing rules; skipped auto-repricing to avoid clobbering prices: ${ruleCountError.message}`
        );
      }

      if (!activeRuleCount || activeRuleCount === 0) {
        console.log(
          `[BookingPricingIntegration] No active pricing rules for ${propertyId}; skipping auto-repricing (prices left untouched).`
        );
        return;
      }

      const workflowService = getUnifiedPricingWorkflowService();

      // Build description for logging
      const description = `auto:${aggregation.bookingsCreated}new/${aggregation.bookingsUpdated}upd/${aggregation.bookingsCancelled}canc`;

      // Small delay to ensure database writes from booking sync are fully committed
      // This prevents race conditions where occupancy data isn't yet available
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Use default look-ahead days for booking-triggered repricing
      // We reprice the standard window rather than trying to scope to specific dates
      // This ensures all rules are properly evaluated with current occupancy
      const result = await workflowService.executeWorkflow(
        propertyId,
        'booking_event',
        description,
        {
          lookAheadDays: PRICING_CONFIG.DEFAULT_LOOK_AHEAD_DAYS,
          minConfidence: 0.6, // Lower threshold for automatic repricing
        }
      );

      // Throw on failure so BookingTriggeredPricingService logs it (and retries
      // a held apply lock) instead of reporting the reprice as completed.
      if (!result.success || !result.data) {
        throw Object.assign(
          new Error(result.error?.message || 'Repricing failed'),
          { details: result.error?.details }
        );
      }
      if (!result.data.success) {
        throw new Error(
          `Repricing finished with errors: ${result.data.errors.map((e) => e.error).join('; ')}`
        );
      }

      console.log(
        `[BookingPricingIntegration] Repricing completed for ${propertyId}:`,
        {
          pricesCalculated: result.data.pricesCalculated,
          pricesPushed: result.data.pricesPushed,
        }
      );
    }
  );

  // Subscribe to booking changes
  unsubscribe = bookingOccupancyService.subscribeToChanges(
    (propertyId, result) => {
      console.log(
        `[BookingPricingIntegration] Received booking change for property ${propertyId}`,
        {
          created: result.bookingsCreated,
          updated: result.bookingsUpdated,
          cancelled: result.bookingsCancelled,
        }
      );

      // Forward to the pricing trigger service (handles debouncing)
      bookingTriggeredPricingService.handleBookingChange(propertyId, result);
    }
  );

  isInitialized = true;
  console.log('[BookingPricingIntegration] Initialized successfully');
}

/**
 * Disable the booking-pricing integration.
 * Stops automatic repricing on booking changes.
 */
export function disableBookingPricingIntegration(): void {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  bookingTriggeredPricingService.setEnabled(false);
  isInitialized = false;
  console.log('[BookingPricingIntegration] Disabled');
}

/**
 * Enable the booking-pricing integration.
 * Resumes automatic repricing on booking changes.
 */
export function enableBookingPricingIntegration(): void {
  bookingTriggeredPricingService.setEnabled(true);
  if (!isInitialized) {
    initializeBookingPricingIntegration();
  }
  console.log('[BookingPricingIntegration] Enabled');
}

/**
 * Check if the integration is currently active
 */
export function isBookingPricingIntegrationActive(): boolean {
  return isInitialized && bookingTriggeredPricingService.isEnabled();
}
